/**
 * Pure helpers for the Inbox review dialogs (patient details changed, medical history from the patient app): which
 * section a detail belongs to, the one-line summary above the review, how a value reads, and how a history item splits
 * into a main and a secondary line.
 */
import { DemographicField } from './referral.model';

export type DemographicSection =
  | 'Personal' | 'Contact' | 'Work' | 'Emergency contact' | 'Second emergency contact' | 'Account holder' | 'Medical aid / gap cover' | 'Other';

/** Every known field, section by section, in the order the invite asks for them. */
const SECTION_FIELDS: [DemographicSection, string[]][] = [
  ['Personal', ['title', 'initials', 'name', 'surname', 'dob', 'gender', 'idType', 'idNumber', 'passportNumber', 'passportCountry',
    'maritalStatus', 'language', 'religion', 'isDeceased', 'dateOfDeath']],
  ['Contact', ['email', 'mobileNumber', 'phoneNumber', 'address', 'addressCode', 'country']],
  ['Work', ['occupation', 'employer', 'workPhone', 'workAddress', 'workAddressCode']],
  ['Emergency contact', ['emergencyContact', 'emergencyContactRelationship', 'emergencyContactMobile', 'emergencyContactPhone',
    'emergencyContactWork', 'emergencyContactEmail', 'emergencyContactAddress', 'emergencyContactAddressCode']],
  ['Second emergency contact', ['emergencyContact2', 'emergencyContactRelationship2', 'emergencyContactMobile2', 'emergencyContactPhone2',
    'emergencyContactWork2', 'emergencyContactEmail2', 'emergencyContactAddress2', 'emergencyContactAddressCode2']],
  ['Account holder', ['liableTitle', 'liableInitials', 'liableName', 'liableSurname', 'liableIdType', 'liableIdNumber',
    'liablePassportNumber', 'liablePassportCountry', 'liableEmail', 'liableMobileNumber', 'liablePhoneNumber', 'liableAddress',
    'liableAddressCode', 'liableOccupation', 'liableEmployer', 'liableWorkPhone', 'liableWorkAddress', 'liableWorkAddressCode']],
  ['Medical aid / gap cover', ['medicalAid', 'medicalSchemeCode', 'medicalAidPlan', 'medicalPlanCode', 'medicalPlanOptionCode',
    'medicalAidNumber', 'medicalMainMember', 'medicalDependantCode', 'medicalDateJoined', 'gapCover', 'gapCoverNumber']],
];

const SECTION_ORDER: DemographicSection[] = [...SECTION_FIELDS.map(([s]) => s), 'Other'];
const FIELD_SECTION = new Map<string, DemographicSection>(SECTION_FIELDS.flatMap(([s, fields]) => fields.map((f) => [f, s] as const)));
const FIELD_ORDER = new Map<string, number>(SECTION_FIELDS.flatMap(([, fields]) => fields).map((f, i) => [f, i]));

export function demographicSection(field: string): DemographicSection {
  return FIELD_SECTION.get(field) ?? 'Other';
}

export interface DemographicGroup {
  section: DemographicSection;
  fields: DemographicField[];
}

/** The fields under their section headings, sections and fields in the invite's order; unknown fields last, as sent. */
export function groupDemographicFields(fields: readonly DemographicField[]): DemographicGroup[] {
  const rank = (f: DemographicField) => FIELD_ORDER.get(f.field) ?? Number.MAX_SAFE_INTEGER;
  return SECTION_ORDER
    .map((section) => ({ section, fields: fields.filter((f) => demographicSection(f.field) === section) }))
    .filter((g) => g.fields.length)
    .map((g) => ({ ...g, fields: [...g.fields].sort((a, b) => rank(a) - rank(b)) }));
}

/** How many of the keys have no decision yet. */
export function undecidedCount(keys: readonly string[], decisions: Record<string, unknown>): number {
  return keys.filter((k) => !decisions[k]).length;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "8 changes · 3 decided", with "· 2 already the same" when some need no decision. */
export function reviewSummary(toDecide: number, decided: number, alreadyDone: number, noun: string, alreadyLabel = 'already the same'): string {
  const parts = toDecide ? [plural(toDecide, noun), `${decided} decided`] : [];
  if (alreadyDone) parts.push(`${alreadyDone} ${alreadyLabel}`);
  return parts.join(' · ');
}

/** A value as a reviewer reads it: an em dash for nothing, Yes/No for the deceased flag. */
export function reviewValue(value: string | boolean | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

type HistoryFields = Record<string, unknown>;

const text = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());
const joined = (values: unknown[], sep: string) => values.map(text).filter(Boolean).join(sep);

const HISTORY_LINES: Record<string, (i: HistoryFields) => { main: string; secondary: string }> = {
  allergies: (i) => ({ main: text(i['allergy']), secondary: text(i['reaction']) }),
  medicalHistory: (i) => ({ main: text(i['surgery']), secondary: text(i['year']) }),
  medicationHistory: (i) => ({ main: text(i['medication']), secondary: text(i['dosage']) }),
  medicalTeam: (i) => ({
    main: joined([i['title'], i['name'], i['surname']], ' '),
    secondary: joined([i['speciality'], i['phone'], i['email']], ' · '),
  }),
};

/** A history item's main text (the allergy, condition, medicine or doctor) and its detail line. */
export function historyItemLines(list: string, item: HistoryFields | null | undefined, described: string): { main: string; secondary: string } {
  const lines = item && HISTORY_LINES[list] ? HISTORY_LINES[list](item) : null;
  return lines && lines.main ? lines : { main: described, secondary: '' };
}
